import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Maximize2, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LightboxGallery } from "@/components/ImageLightbox";
import { cn, shuffleArray } from "@/lib/utils";
import interierBB from "@/assets/projects/interier-bb-01.webp";
import interierMS from "@/assets/projects/interier-ms-01.webp";
import interierUS from "@/assets/projects/interier-us-01.webp";
import interierL from "@/assets/projects/interier-l-01.png";
import kitchenKT from "@/assets/projects/kuhinja-kt-02.webp";
import kitchenS from "@/assets/projects/kitchen-s-01.webp";
import apartmentU from "@/assets/projects/apartment-umag-01.jpg";
import vetClinic from "@/assets/projects/vet-clinic-01.jpg";
import tcgPark from "@/assets/projects/tcg-park-01.jpg";
import interierMM from "@/assets/projects/interier-mm-01.webp.asset.json";
import ghPortoroz from "@/assets/projects/gh-portoroz-01.webp.asset.json";

const completedProjects = [
  { title: "Interier BB", slug: "interier-bb", src: interierBB },
  { title: "Interier MS", slug: "interier-ms", src: interierMS },
  { title: "Interier US", slug: "interier-us", src: interierUS },
  { title: "Interier L", slug: "interier-l", src: interierL },
  { title: "Interier MM", slug: "interier-mm", src: interierMM.url },
  { title: "GH Portorož", slug: "gh-portoroz", src: ghPortoroz.url },
  { title: "Kuhinja KT", slug: "kuhinja-kt", src: kitchenKT },
  { title: "Kuhinja S", slug: "kuhinja-s", src: kitchenS },
  { title: "Stanovanje U", slug: "stanovanje-u", src: apartmentU },
  { title: "Veterinarska ambulanta", slug: "veterinarska-ambulanta", src: vetClinic },
  { title: "TCG Park Supernova", slug: "tcg-park", src: tcgPark },
];

export function CompletedProjectsSlideshow() {
  const projects = useMemo(() => shuffleArray(completedProjects), []);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const current = projects[index];

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (paused || interacting || lightboxOpen || reducedMotion) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setIndex((previous) => (previous + 1) % projects.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, interacting, lightboxOpen, reducedMotion, projects.length]);

  if (!current) return null;

  const move = (direction: number) => {
    setIndex((previous) => (previous + direction + projects.length) % projects.length);
  };

  return (
    <section
      className="py-20 md:py-32 bg-background"
      aria-label="Končani projekti"
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
      onFocusCapture={() => setInteracting(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setInteracting(false);
      }}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted md:aspect-video">

          {projects.map((project, imageIndex) => (
            <img
              key={project.slug}
              src={project.src}
              alt={project.title}
              aria-hidden={imageIndex !== index}
              className={cn(
                "absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 motion-reduce:transition-none",
                imageIndex === index ? "opacity-100" : "opacity-0"
              )}
              loading={imageIndex === index ? "eager" : "lazy"}
            />
          ))}
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-4 top-4 bg-background/90 hover:bg-background md:right-8 md:top-8"
            aria-label="Povečaj fotografijo"
            title="Povečaj fotografijo"
            onClick={() => setLightboxOpen(true)}
          >
            <Maximize2 />
          </Button>
      </div>
      <div className="container">
        <div className="flex items-center justify-between gap-3 pt-4">

          <Link
            to={`/projekti/${current.slug}`}
            className="inline-flex min-w-0 items-center gap-2 text-foreground hover:text-bronze transition-colors"
          >
            <span className="font-serif text-lg">{current.title}</span>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </Link>
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="icon" aria-label="Prejšnji projekt" title="Prejšnji projekt" onClick={() => move(-1)}>
              <ChevronLeft />
            </Button>
            {!reducedMotion && (
              <Button variant="ghost" size="icon" aria-label={paused ? "Začni menjavanje slik" : "Ustavi menjavanje slik"} title={paused ? "Začni menjavanje slik" : "Ustavi menjavanje slik"} onClick={() => setPaused((value) => !value)}>
                {paused ? <Play /> : <Pause />}
              </Button>
            )}
            <Button variant="ghost" size="icon" aria-label="Naslednji projekt" title="Naslednji projekt" onClick={() => move(1)}>
              <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
      <LightboxGallery
        images={projects.map((project) => ({ src: project.src, alt: project.title }))}
        initialIndex={index}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </section>
  );
}